import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  onSnapshot,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebaseClient';

/**
 * Firestore Helper - Adaptador para facilitar la migración de Base44 a Firebase
 * 
 * Esta clase proporciona una API similar a Base44 pero usando Firebase Firestore.
 * Permite migrar componentes gradualmente sin cambiar toda la lógica de negocio.
 */

class FirestoreEntityHelper {
  constructor(collectionName) {
    this.collectionName = collectionName;
    this.collectionRef = collection(db, collectionName);
  }

  /**
   * Listar todos los documentos de la colección con ordenamiento opcional
   * @param {string} sortField - Campo para ordenar (prefijo '-' para descendente)
   * @returns {Promise<Array>} Array de documentos
   */
  async list(sortField = null) {
    try {
      let q = this.collectionRef;
      
      if (sortField) {
        const isDescending = sortField.startsWith('-');
        const field = isDescending ? sortField.substring(1) : sortField;
        const direction = isDescending ? 'desc' : 'asc';
        q = query(this.collectionRef, orderBy(field, direction));
      }
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...this.deserializeDoc(doc.data())
      }));
    } catch (error) {
      console.error(`Error listing ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Filtrar documentos con condiciones y ordenamiento
   * @param {Object} filters - Objeto con filtros { campo: valor }
   * @param {string} sortField - Campo para ordenar (opcional)
   * @returns {Promise<Array>} Array de documentos filtrados
   */
  async filter(filters = {}, sortField = null) {
    try {
      let q = this.collectionRef;
      
      // Aplicar filtros
      Object.entries(filters).forEach(([field, value]) => {
        q = query(q, where(field, '==', value));
      });
      
      // Aplicar ordenamiento
      if (sortField) {
        const isDescending = sortField.startsWith('-');
        const field = isDescending ? sortField.substring(1) : sortField;
        const direction = isDescending ? 'desc' : 'asc';
        q = query(q, orderBy(field, direction));
      }
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...this.deserializeDoc(doc.data())
      }));
    } catch (error) {
      console.error(`Error filtering ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Obtener un documento por ID
   * @param {string} id - ID del documento
   * @returns {Promise<Object>} Documento
   */
  async get(id) {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...this.deserializeDoc(docSnap.data())
        };
      } else {
        throw new Error(`Document ${id} not found in ${this.collectionName}`);
      }
    } catch (error) {
      console.error(`Error getting document ${id} from ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Crear un nuevo documento
   * @param {Object} data - Datos del documento
   * @returns {Promise<Object>} Documento creado con ID
   */
  async create(data) {
    try {
      const serializedData = this.serializeDoc({
        ...data,
        created_date: serverTimestamp(),
        updated_date: serverTimestamp()
      });
      
      const docRef = await addDoc(this.collectionRef, serializedData);
      const newDoc = await this.get(docRef.id);
      return newDoc;
    } catch (error) {
      console.error(`Error creating document in ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Actualizar un documento existente
   * @param {string} id - ID del documento
   * @param {Object} data - Datos a actualizar
   * @returns {Promise<Object>} Documento actualizado
   */
  async update(id, data) {
    try {
      const docRef = doc(db, this.collectionName, id);
      const serializedData = this.serializeDoc({
        ...data,
        updated_date: serverTimestamp()
      });
      
      await updateDoc(docRef, serializedData);
      const updatedDoc = await this.get(id);
      return updatedDoc;
    } catch (error) {
      console.error(`Error updating document ${id} in ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Eliminar un documento
   * @param {string} id - ID del documento
   * @returns {Promise<void>}
   */
  async delete(id) {
    try {
      const docRef = doc(db, this.collectionName, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error(`Error deleting document ${id} from ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Suscribirse a cambios en tiempo real
   * @param {Function} callback - Función que se llama cuando hay cambios
   * @returns {Function} Función para cancelar la suscripción
   */
  subscribe(callback) {
    try {
      const unsubscribe = onSnapshot(this.collectionRef, (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          const docData = {
            id: change.doc.id,
            ...this.deserializeDoc(change.doc.data())
          };
          
          callback({
            type: change.type, // 'added', 'modified', 'removed'
            data: docData
          });
        });
      });
      
      return unsubscribe;
    } catch (error) {
      console.error(`Error subscribing to ${this.collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Serializar datos antes de guardar en Firestore
   * Convierte fechas ISO string a Timestamp de Firestore
   */
  serializeDoc(data) {
    const serialized = { ...data };
    
    // Convertir fechas ISO a Timestamps
    Object.keys(serialized).forEach(key => {
      const value = serialized[key];
      
      // Si es una fecha ISO string, convertir a Timestamp
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
        try {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            serialized[key] = Timestamp.fromDate(date);
          }
        } catch (e) {
          // Mantener el valor original si falla la conversión
        }
      }
    });
    
    return serialized;
  }

  /**
   * Deserializar datos al leer de Firestore
   * Convierte Timestamps a ISO strings
   */
  deserializeDoc(data) {
    const deserialized = { ...data };
    
    // Convertir Timestamps a ISO strings
    Object.keys(deserialized).forEach(key => {
      const value = deserialized[key];
      
      if (value instanceof Timestamp) {
        deserialized[key] = value.toDate().toISOString();
      }
    });
    
    return deserialized;
  }
}

/**
 * Crear entidades de forma dinámica
 */
export const createEntity = (collectionName) => {
  return new FirestoreEntityHelper(collectionName);
};

/**
 * Entidades predefinidas para el CRM
 */
export const entities = {
  Lead: createEntity('leads'),
  Presupuesto: createEntity('presupuestos'),
  Categoria: createEntity('categorias'),
  Producto: createEntity('productos'),
  Settings: createEntity('settings'),
  EmailMessage: createEntity('emailMessages'),
  Actividad: createEntity('actividades'),
  EmailDraft: createEntity('emailDrafts'),
  SeguimientoEmail: createEntity('seguimientosEmail'),
  DiscordAlert: createEntity('discordAlerts'),
  WebhookConfig: createEntity('webhookConfigs'),
  RoadmapItem: createEntity('roadmapItems'),
  CatalogoTruyol: createEntity('catalogo_truyol'),
  CatalogoTruyolMaestro: createEntity('catalogo_truyol_maestro'),
};

export default entities;
